import {
  Configuration,
  PublicClientApplication,
  LogLevel,
  BrowserCacheLocation,
} from '@azure/msal-browser';

/**
 * MSAL configuration for Azure AD Multi-Tenant authentication.
 * Users sign in with their Microsoft 365 organizational accounts.
 * Readiness captures user + tenant context for analysis scoping.
 */
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || '',
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: `${import.meta.env.VITE_REDIRECT_URI || window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes for API access.
 *
 * Sign-in requests ONLY the API scope plus User.Read — both user-consentable,
 * NO admin consent required. This keeps the default (Mode A, form-only) report
 * frictionless. SharePoint scopes are NOT requested here; they are acquired
 * incrementally only if the user opts into an environment scan (Mode B).
 * See ``graphSharePointScopes``.
 */
export const loginRequest = {
  scopes: [
    `api://${import.meta.env.VITE_AZURE_AD_CLIENT_ID}/access_as_user`,
  ],
  // User.Read only — personalises the report, no admin consent required.
  extraScopesToConsent: [
    'User.Read',
  ],
};

/**
 * Scopes for silent token acquisition
 */
export const tokenRequest = {
  scopes: [
    `api://${import.meta.env.VITE_AZURE_AD_CLIENT_ID}/access_as_user`,
  ],
};

/**
 * Graph scope for the opt-in SharePoint environment scan (Mode B).
 *
 * Sites.Read.All is an admin-consent-required delegated permission — a normal
 * user cannot self-consent. The opt-in flow requests it incrementally; if the
 * signed-in user is not a tenant admin, they are routed through the
 * admin-approval request flow (see ``useSharePointConsent``).
 *
 * Sites.Read.All also covers SharePoint document-library drives, so Files.Read.All
 * is intentionally NOT requested — keeping the admin-consent ask to a single scope.
 */
export const graphSharePointScopes = {
  scopes: [
    'Sites.Read.All',
  ],
};

/**
 * Graph API scopes for Readiness analysis (Deep tier — admin consent required)
 */
export const graphDeepScopes = {
  scopes: [
    'User.Read',
    'Sites.Read.All',
    'Reports.Read.All',
    'Organization.Read.All',
    'SecurityEvents.Read.All',
    'Policy.Read.All',
    'AuditLog.Read.All',
    'User.Read.All',
  ],
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL instance — must be called before using any MSAL functionality
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();

  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);

        try {
          await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account: accounts[0],
          });
        } catch {
          msalInstance.setActiveAccount(null);
          clearMsalCache();
        }
      }
    }
  } catch {
    clearMsalCache();
  }
}

function clearMsalCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}
